import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { Flag } from '@mui/icons-material';
import ArcadeGameShell from '../ArcadeGameShell';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { ManualPlayChrome, useManualPlay, usePlayKeyboard } from '../manualPlay';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { useManualRunRecorder } from '../useManualRunRecorder';
import {
    createMinesSession,
    deleteMinesSession,
    moveMinesSession,
    CELL_POINTS,
    VICTORY_BONUS,
    computeMinesweeperScore,
} from './minesGmClient';
import { useTransientMessage } from '../useTransientMessage';
import { gmStartErrorMessage } from '../gmLease';
import { COLOR, GAME_ACCENT } from '../ui/tokens';

const GAME_ID = 'minesweeper';
const { UPLOAD_URL } = ARCADE.minesweeper;
const ACCENT = GAME_ACCENT.minesweeper;
const ROWS = 9;
const COLS = 9;
const MINES = 10;
const HINT = 'Arrows / WASD move · Enter reveal · F / Space flag · Esc pause';

export { CELL_POINTS, VICTORY_BONUS, computeMinesweeperScore };

const CELL_COLORS = {
    '?': 'rgba(255,255,255,0.05)', F: 'rgba(255,204,0,0.7)', M: 'rgba(244,67,54,0.7)',
    0: 'rgba(33,150,243,0.15)', 1: '#4FC3F7', 2: '#66BB6A', 3: '#FF7043',
    4: '#AB47BC', 5: '#EF5350', 6: '#26C6DA', 7: '#FFCA28', 8: '#A1887F',
};

const blankBoard = () => Array.from({ length: ROWS }, () => Array(COLS).fill('?'));

const Minesweeper = () => {
    const play = useManualPlay();
    const sessionIdRef = useRef(null);
    const [visibleBoard, setVisibleBoard] = useState(blankBoard);
    const [moves, setMoves] = useState(0);
    const [flagsUsed, setFlagsUsed] = useState(0);
    const [, setRevealedSafe] = useState(0); // value not rendered; setter kept so updates behave as before
    const [score, setScore] = useState(0);
    const [isGameOver, setIsGameOver] = useState(false);
    const [isVictory, setIsVictory] = useState(false);
    const [cursor, setCursor] = useState({ row: 0, col: 0 });
    const [flagMode, setFlagMode] = useState(false);
    const [message, setMessage] = useTransientMessage();
    const [busy, setBusy] = useState(false);

    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const recorder = useManualRunRecorder({ gameId: GAME_ID, session });

    const applyGm = useCallback((data) => {
        if (Array.isArray(data.visible_board)) {
            setVisibleBoard(data.visible_board.map((r) => r.slice()));
        }
        if (typeof data.moves === 'number') setMoves(data.moves);
        if (typeof data.flags_used === 'number') setFlagsUsed(data.flags_used);
        if (typeof data.revealed_safe === 'number') setRevealedSafe(data.revealed_safe);
        if (typeof data.score === 'number') setScore(data.score);
        setIsGameOver(Boolean(data.is_game_over));
        setIsVictory(Boolean(data.is_victory));
        setMessage(data.message || '');
        return {
            over: Boolean(data.is_game_over),
            score: typeof data.score === 'number' ? data.score : 0,
            victory: Boolean(data.is_victory),
            revealedSafe: typeof data.revealed_safe === 'number' ? data.revealed_safe : 0,
        };
    }, [setMessage]);

    const initFromGm = useCallback(async () => {
        if (sessionIdRef.current) {
            await deleteMinesSession(sessionIdRef.current);
            sessionIdRef.current = null;
        }
        const data = await createMinesSession();
        if (!data?.session_id) throw new Error('Minesweeper GM missing session_id');
        sessionIdRef.current = data.session_id;
        applyGm(data);
        setCursor({ row: 0, col: 0 });
        setFlagMode(false);
        recorder.reset({ rows: ROWS, cols: COLS });
    }, [applyGm, recorder]);

    const startGame = useCallback(async () => {
        try {
            setBusy(true);
            await initFromGm();
            play.startGame();
        } catch (err) {
            console.error(err);
            setMessage(gmStartErrorMessage(err));
        } finally {
            setBusy(false);
        }
    }, [initFromGm, play, setMessage]);

    const endGame = useCallback(async () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        await deleteMinesSession(sid);
        play.endGame({ score });
        setVisibleBoard(blankBoard());
        setMoves(0);
        setFlagsUsed(0);
        setRevealedSafe(0);
        setScore(0);
        setIsGameOver(false);
        setIsVictory(false);
        setFlagMode(false);
        setMessage('');
    }, [play, setMessage, score]);

    useEffect(() => () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        deleteMinesSession(sid);
    }, []);

    const act = useCallback(async (row, col, action) => {
        if (play.phase !== 'playing' || isGameOver || busy) return;
        const sid = sessionIdRef.current;
        if (!sid) return;
        setBusy(true);
        try {
            const data = await moveMinesSession(sid, { action, row, col });
            const meta = applyGm(data);
            const last = Array.isArray(data.history) ? data.history[data.history.length - 1] : null;
            recorder.recordTick({
                action,
                row,
                col,
                result: last?.result,
                state: last?.state,
                revealed_safe: meta.revealedSafe,
                score: meta.score,
                visible_board: data.visible_board,
            });
            if (meta.over) {
                play.markOver();
                recorder.finalizeAndUpload({
                    score: meta.score,
                    extra: { rows: ROWS, cols: COLS, revealed_safe: meta.revealedSafe },
                });
                const done = sessionIdRef.current;
                sessionIdRef.current = null;
                deleteMinesSession(done);
            }
        } catch (err) {
            console.warn('Minesweeper GM move failed', err);
            setMessage(err?.data?.message || 'Move failed');
        } finally {
            setBusy(false);
        }
    }, [applyGm, busy, isGameOver, play, recorder, setMessage]);

    const reveal = useCallback((row, col) => act(row, col, 'reveal'), [act]);
    const toggleFlag = useCallback((row, col) => act(row, col, 'flag'), [act]);
    const clickAct = useCallback((row, col) => {
        if (flagMode) toggleFlag(row, col);
        else reveal(row, col);
    }, [flagMode, reveal, toggleFlag]);

    const onKey = useCallback((e) => {
        if (['ArrowUp', 'w', 'W'].includes(e.key)) {
            setCursor((c) => ({ ...c, row: Math.max(0, c.row - 1) }));
            e.preventDefault();
        } else if (['ArrowDown', 's', 'S'].includes(e.key)) {
            setCursor((c) => ({ ...c, row: Math.min(ROWS - 1, c.row + 1) }));
            e.preventDefault();
        } else if (['ArrowLeft', 'a', 'A'].includes(e.key)) {
            setCursor((c) => ({ ...c, col: Math.max(0, c.col - 1) }));
            e.preventDefault();
        } else if (['ArrowRight', 'd', 'D'].includes(e.key)) {
            setCursor((c) => ({ ...c, col: Math.min(COLS - 1, c.col + 1) }));
            e.preventDefault();
        } else if (e.key === 'f' || e.key === 'F') {
            setFlagMode((m) => !m);
            e.preventDefault();
        } else if (e.key === ' ') {
            toggleFlag(cursor.row, cursor.col);
            e.preventDefault();
        } else if (e.key === 'Enter') {
            reveal(cursor.row, cursor.col);
            e.preventDefault();
        }
    }, [cursor, reveal, toggleFlag]);

    usePlayKeyboard({
        phase: play.phase,
        togglePause: play.togglePause,
        onKey,
        deps: [cursor, reveal, toggleFlag],
    });

    const renderCellLabel = (value) => {
        if (value === '0' || value === '?') return '';
        if (value === 'F') return '⚑';
        if (value === 'M') return isGameOver ? '✱' : '';
        return value;
    };

    return (
        <ArcadeGameShell
            title="Minesweeper"
            titleAccent="Clear"
            subtitle="Reveal safe cells — play manually or upload a solver"
            accentColor={ACCENT}
            boilerplate={GAME_BOILERPLATE.minesweeper}
            session={session}
            formatScore={(e) => String(e.score ?? e.revealed_safe ?? 0)}
        >
            <ManualPlayChrome
                phase={play.phase}
                result={play.result}
                keyboardStart={false}
                onStart={startGame}
                onPause={play.togglePause}
                onEnd={endGame}
                accentColor={ACCENT}
                controlsHint={HINT}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip
                            icon={<Flag sx={{ fontSize: 14 }} />}
                            label={flagMode ? 'Flag mode' : 'Reveal mode'}
                            size="small"
                            onClick={() => setFlagMode((m) => !m)}
                            sx={{ color: flagMode ? '#FFCA28' : ACCENT, borderColor: flagMode ? '#FFCA28' : ACCENT }}
                            variant="outlined"
                        />
                        <Chip label={`Moves: ${moves}`} size="small" sx={{ color: ACCENT, borderColor: ACCENT }} variant="outlined" />
                        <Chip label={`Flags: ${flagsUsed}/${MINES}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={`Score: ${score}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip
                            label={isVictory ? 'Cleared' : isGameOver ? 'Boom' : 'Playing'}
                            size="small"
                            color={isVictory ? 'success' : isGameOver ? 'error' : 'warning'}
                        />
                    </Stack>
                    {message && <Typography sx={{ color: COLOR.textMuted, mb: 1, fontSize: '0.85rem' }}>{message}</Typography>}
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${COLS}, 1fr)`,
                        gap: 0.5,
                        p: 1.5,
                        maxWidth: 420,
                        mx: 'auto',
                        borderRadius: 2,
                        border: '1px solid rgba(79,195,247,0.25)',
                    }}
                    >
                        {visibleBoard.map((row, rIdx) => row.map((value, cIdx) => {
                            const focused = play.isActive && cursor.row === rIdx && cursor.col === cIdx;
                            return (
                                <Box
                                    key={`${rIdx}-${cIdx}`}
                                    onClick={() => {
                                        if (play.phase !== 'playing' || busy) return;
                                        setCursor({ row: rIdx, col: cIdx });
                                        clickAct(rIdx, cIdx);
                                    }}
                                    onContextMenu={(e) => {
                                        e.preventDefault();
                                        if (play.phase !== 'playing' || busy) return;
                                        toggleFlag(rIdx, cIdx);
                                    }}
                                    sx={{
                                        aspectRatio: '1',
                                        borderRadius: 0.5,
                                        background: CELL_COLORS[value] || CELL_COLORS['?'],
                                        border: focused ? `2px solid ${ACCENT}` : '1px solid rgba(255,255,255,0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: value === '?' ? 'rgba(255,255,255,0.5)' : '#0D1117',
                                        fontWeight: 'bold',
                                        fontSize: '0.85rem',
                                        cursor: isGameOver || play.phase !== 'playing' ? 'default' : 'pointer',
                                    }}
                                >
                                    {renderCellLabel(value)}
                                </Box>
                            );
                        }))}
                    </Box>
                </Box>
            </ManualPlayChrome>
        </ArcadeGameShell>
    );
};

export default Minesweeper;
